import { memo, useEffect, useState } from 'react';
import { CompactPicker } from 'react-color';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Card, ConfigProvider, Flex, Form, Input, Select } from 'antd';

import classnames from 'classnames';
import equal from 'fast-deep-equal';

import { capitalize } from '../../../helpers/common';
import { mapGroupsToRules, mapRulesToGroups } from '../../../helpers/group';

import { FORM_FIELD_TYPE } from '../../../constants/common';
import { MAIN_TYPE_OPTIONS, MAIN_GROUP_TYPE } from '../../../constants/group';

import { fetchFarms } from '../../../redux/actions/farm';

import GroupQueryBuilder from '../../../components/Group/GroupQueryBuilder';
import SelectedGroups from '../../../components/Group/SelectedGroups';

import { GROUP_EDIT_FIELDS } from './constants';
import messages from '../../../constants/messages';

const Groups = ({ editing, groups, group, query, handleQueryChange }) => {
  // For backword compatability with old collection groups
  const q =
    !query && group?.collection?.length
      ? { combinator: 'AND', rules: group?.collection?.map(c => ({ field: 'name', operator: '=', value: c.id })) }
      : query;

  return (
    <Flex className="qb-container" vertical={false} style={{ background: 'rgba(0, 0, 0, 0.015)' }}>
      <Flex vertical={true} className="qb-label" style={{ padding: editing ? 0 : '5px 0' }}>
        {editing ? 'Group Builder' : 'Groups'}
        {editing && <SelectedGroups options={groups} query={q} />}
      </Flex>
      <Flex className="qb-builder" style={{ paddingLeft: editing ? 0 : '12px' }}>
        {editing && <GroupQueryBuilder options={groups} query={q} handleQueryChange={handleQueryChange} />}
        {!editing && <SelectedGroups options={groups} query={q} />}
      </Flex>
    </Flex>
  );
};

function GroupEditForm({
  color,
  editing,
  formRef,
  group,
  groups,
  handleChangeFormValues,
  handleQueryBuilderChange,
  onChange,
  setSubmittable
}) {
  const [form] = Form.useForm();

  const dispatch = useDispatch();
  const { options } = useSelector(state => state.farm);

  const [initialFormValues, setInitialFormValues] = useState({});
  const [currentColor, setCurrentColor] = useState();
  const [groupType, setGroupType] = useState(group?.main_type);
  const [selectedFarm, setSelectedFarm] = useState(group?.farm?.id);
  const [query, setQuery] = useState({});

  const [mainTypeOptions, setMainTypeOptions] = useState(MAIN_TYPE_OPTIONS);

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  useEffect(() => {
    dispatch(fetchFarms());

    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [values]);

  useEffect(() => {
    if (color) setCurrentColor(color);
  }, [color]);

  useEffect(() => {
    if (group) {
      setGroupType(group?.main_type);

      let filters;
      if (typeof group.filters === 'string') {
        filters = JSON.parse(group?.filters);
      } else {
        filters = group.filters;
      }
      const query = mapGroupsToRules(filters);
      setQuery(query);
    }
  }, [group, editing]);

  useEffect(() => {
    if (!group) {
      return;
    }

    setSelectedFarm(group?.farm?.id);
  }, [group?.farm?.id]);

  useEffect(() => {
    if (group) {
      form.setFieldsValue(prepareFormValues(GROUP_EDIT_FIELDS));
    }
  }, [group, groupType, selectedFarm]);

  useEffect(() => {
    let updatedOptions = MAIN_TYPE_OPTIONS;
    // "Collection" type should be not available on editing of the physical or virtual group
    if (editing && groupType !== MAIN_GROUP_TYPE.COLLECTION) {
      updatedOptions = MAIN_TYPE_OPTIONS.filter(option => option.value !== MAIN_GROUP_TYPE.COLLECTION);
    }
  
    setMainTypeOptions(updatedOptions);
  }, [editing]);

  function prepareFormValues(formFields) {
    if (!formFields) return;

    const fields = [{ id: 'identifier' }, { id: 'name' }, { id: 'main_type' }];
    const allFields = [...formFields, ...fields];
    const initValues = {};

    for (const field of allFields) {
      let value;

      switch (field.id) {
        case 'farm_id':
          value = selectedFarm;
          break;
        case 'main_type':
          value = capitalize(groupType);
          break;
        default:
          value = group?.[field.id] ? group?.[field.id] : field.type === FORM_FIELD_TYPE.NUMBER ? 0 : '';
      }

      initValues[field.id] = value;
    }

    return initValues;
  }

  const getAssignedAnimalsList = () => {
    const animals = group?.animals;
    const assignedAnimals = [];

    for (const animal of animals) {
      if (animal?.labels?.every(l => l.main_type !== 'physical')) {
        assignedAnimals.push(animal);
      }
    }

    return assignedAnimals;
  };

  function showSelect(field) {
    return field.type === FORM_FIELD_TYPE.SELECT && !(field.id === 'farm_id' && !editing);
  }

  function handleChange(changedValues) {
    if (changedValues?.farm_id) {
      setSelectedFarm(changedValues.farm_id);
    }

    if (changedValues?.main_type) {
      setGroupType(changedValues?.main_type);
    }

    if (changedValues?.colour) {
      setCurrentColor(changedValues?.colour?.hex);
    }

    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
    handleChangeFormValues(changedValues?.colour ? { colour: changedValues?.colour?.hex } : changedValues);
  }

  function handleQueryChange(query) {
    const payload = {
      operator: query.combinator,
      groups: mapRulesToGroups(query)
    };

    setQuery(query);
    handleQueryBuilderChange(payload);
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorTextDisabled: 'rgba(0,0,0,0.88)'
        },
        components: {
          Select: {
            multipleSelectorBgDisabled: 'transparent',
            multipleItemBg: 'white'
          }
        }
      }}>
      <Card title={`Group ${group?.name} Details`} className="mb-2">
        <Form
          className="group-edit-form"
          name="group-edit"
          form={form}
          initialValues={prepareFormValues()}
          labelCol={{ span: 5 }}
          requiredMark={false}
          autoComplete="off"
          onValuesChange={handleChange}>
          <Flex vertical={true} gap="small">
            <Form.Item labelWrap key="identifier" label="Group ID" name="identifier" colon={false} labelAlign="left">
              <Input disabled variant="borderless" />
            </Form.Item>
            <Form.Item
              labelWrap
              key="name"
              label="Group Name"
              name="name"
              colon={false}
              labelAlign="left"
              rules={[
                {
                  required: true,
                  message: messages.FIELD_REQUIRED('Group Name'),
                },
              ]}
            >
              <Input disabled={!editing} variant={editing ? 'outlined' : 'borderless'}/>
            </Form.Item>
            <Form.Item labelWrap key="main_type" label="Group Type" name="main_type" colon={false} labelAlign="left">
              <Select
                className={classnames('select', { 'hidden-arrow': !editing })}
                disabled={(groupType === MAIN_GROUP_TYPE.COLLECTION) || !editing} // change of group for "Collection" is disabled
                options={mainTypeOptions}
                variant={(groupType !== MAIN_GROUP_TYPE.COLLECTION) && editing ? 'outlined' : 'borderless'}
              />
            </Form.Item>

            {groupType === 'collection' && (
              <Groups
                editing={editing}
                groups={groups}
                group={group}
                query={query}
                handleQueryChange={handleQueryChange}
              />
            )}

            {GROUP_EDIT_FIELDS?.map(field => {
              return (
                <Form.Item
                  labelWrap
                  key={field.id}
                  label={field.name}
                  name={field.id}
                  colon={false}
                  labelAlign="left"
                  rules={field.rules}>
                  {showSelect(field) ? (
                    <Select
                      className={classnames('select', { 'hidden-arrow': !editing })}
                      disabled={!editing}
                      mode={field.mode}
                      name={field.id}
                      options={field.id === 'main_type' ? mainTypeOptions : options}
                      variant={editing ? 'outlined' : 'borderless'}
                    />
                  ) : field.id === 'farm_id' && !editing ? (
                    <Link className="field-box" to={`/farms/${group?.farm?.id}`}>
                      {group?.farm?.name}
                    </Link>
                  ) : (
                    <Input
                      disabled={!editing || !field.editable}
                      name={field.id}
                      variant={editing && field.editable ? 'outlined' : 'borderless'}
                    />
                  )}
                </Form.Item>
              );
            })}

            <Form.Item label="Group Color" labelAlign="left" name="colour" colon={false}>
              {editing ? (
                <CompactPicker name="colour" color={currentColor} />
              ) : (
                <div className="color-badge" style={{ background: color }} title={color}></div>
              )}
            </Form.Item>
          </Flex>
        </Form>
      </Card>
    </ConfigProvider>
  );
}

export default memo(GroupEditForm);
